
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Send, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const QuestionSubmissionCard = () => {
  const { t } = useTranslation();
  const [question, setQuestion] = useState('');
  const [context, setContext] = useState('');
  const [senderEmail, setSenderEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!question.trim()) {
      toast.error(t('questionSubmission.questionRequired'));
      return;
    }

    if (question.trim().length < 10) {
      toast.error(t('questionSubmission.questionTooShort'));
      return;
    }

    setIsSubmitting(true);

    try {
      // Combine question and context into a single message
      const message = context.trim() 
        ? `${question.trim()}\n\n${t('questionSubmission.contextLabel')}: ${context.trim()}`
        : question.trim();

      const { data, error } = await supabase.functions.invoke('send-suggestion', {
        body: { 
          message,
          senderEmail: senderEmail.trim() || undefined,
          senderName: senderEmail.trim() ? undefined : t('questionSubmission.anonymousUser')
        }
      });

      if (error) {
        console.error('Error submitting question:', error);
        toast.error(t('questionSubmission.submitError'));
        return;
      }

      console.log('Question submitted successfully:', data);
      toast.success(t('questionSubmission.submitSuccess'));
      
      // Reset form
      setQuestion('');
      setContext('');
      setSenderEmail('');
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      toast.error(t('questionSubmission.unexpectedError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 mb-6 border-l-4 border-[#ea384c]">
      <h2 className="text-xl font-semibold mb-4">{t('questionSubmission.title')}</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="context" className="block text-sm font-medium text-gray-700 mb-1">
            {t('questionSubmission.context')}
          </label>
          <textarea
            id="context"
            value={context}
            onChange={(e) => setContext(e.target.value)}
            placeholder={t('questionSubmission.contextPlaceholder')}
            className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ea384c] focus:border-transparent"
            rows={3}
          />
        </div>
        <div>
          <label htmlFor="question" className="block text-sm font-medium text-gray-700 mb-1">
            {t('questionSubmission.question')} *
          </label>
          <textarea
            id="question"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder={t('questionSubmission.questionPlaceholder')}
            className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ea384c] focus:border-transparent"
            rows={3}
            maxLength={500}
            disabled={isSubmitting}
          />
          <p className="text-xs text-gray-500 mt-1">{question.length}/500 {t('questionSubmission.characters')}</p>
        </div>
        <div>
          <label htmlFor="senderEmail" className="block text-sm font-medium text-gray-700 mb-1">
            {t('questionSubmission.email')}
          </label>
          <input
            type="email"
            id="senderEmail"
            value={senderEmail}
            onChange={(e) => setSenderEmail(e.target.value)}
            placeholder={t('questionSubmission.emailPlaceholder')}
            className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ea384c] focus:border-transparent"
            disabled={isSubmitting}
          />
          <p className="text-xs text-gray-500 mt-1">{t('questionSubmission.emailHelp')}</p>
        </div>
        <div>
          <Button
            type="submit"
            disabled={isSubmitting || !question.trim()}
            className="w-full bg-[#ea384c] text-white px-4 py-2 rounded-lg hover:bg-[#ea384c]/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('questionSubmission.submitting')}
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                {t('questionSubmission.submitQuestion')}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default QuestionSubmissionCard;
